import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { type BackgroundTask, tasksApi } from '../../api/client'
import { fmtTime } from '../../lib/format'

// useTasks: the controller's background tasks - every 2 s while one runs,
// else every 10 s. onDone hears each task that ended since the last look
// (to read again what it changed).
export function useTasks(onDone?: (t: BackgroundTask) => void) {
  const { data } = useQuery({ queryKey: ['tasks'], queryFn: tasksApi.list, refetchInterval: (q) => (q.state.data?.some((t) => t.state === 'running') ? 2000 : 10000) })
  const running = useRef(new Set<number>())
  useEffect(() => {
    for (const t of data || []) {
      if (t.state === 'running') running.current.add(t.id)
      else if (running.current.delete(t.id)) onDone?.(t)
    }
  }, [data, onDone])
  return data || []
}

// TaskList: the tasks of some kinds - running with step and progress, then
// how they ended (the last 30 minutes) - for everyone on the page.
export function TaskList({ tasks }: { tasks: BackgroundTask[] }) {
  if (tasks.length === 0) return null
  return (
    <div className="space-y-1.5">
      {tasks.map((t) => (
        <div key={t.id} className={`rounded-[10px] border px-3 py-2 text-sm ${t.state === 'running' ? 'border-[#9DBBD0] bg-secondary' : t.state === 'failed' ? 'border-[#E8B4AE] bg-[#FBE9E7] text-[#A3271D]' : 'border-[#B5DCC5] bg-[#E2F3E9] text-[#146C43]'}`}>
          <div className="flex flex-wrap items-center gap-x-2">
            {t.state === 'running' && <span aria-hidden className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#2A78D6]" />}
            <b>{t.title}</b>
            <span className="text-xs">{t.state === 'running' ? t.step : t.state === 'failed' ? 'failed' : t.result || 'done'}</span>
            <span className="ml-auto text-xs opacity-80">{t.state === 'running' ? `since ${fmtTime(t.started_at).slice(11)}` : fmtTime(t.finished_at)}{t.by ? ` · ${t.by}` : ''}</span>
          </div>
          {t.state === 'running' && t.progress >= 0 && (
            <div className="mt-1 h-1.5 overflow-hidden rounded bg-card"><div className="h-full rounded bg-primary transition-all" style={{ width: `${t.progress}%` }} /></div>
          )}
          {t.state === 'failed' && t.error && <p className="mt-0.5 break-words text-xs">{t.error}</p>}
        </div>
      ))}
    </div>
  )
}
